/**
 * Section dépliable pour les avenants et remises (affichage + suppression uniquement)
 */

import { useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { formatMontant, formatDate } from '@/lib/formatters';
import type { AjustementPaiement, TypeAjustement } from '@/types/paiement';


interface AjustementsSectionProps {
  type: TypeAjustement;
  ajustements: AjustementPaiement[];
  onDelete: (id: string) => Promise<boolean>;
  isDisabled?: boolean;
}

export function AjustementsSection({
  type,
  ajustements,
  onDelete,
  isDisabled = false,
}: AjustementsSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Labels selon le type
  const label = type === 'avenant' ? 'Avenants' : 'Remises';
  const labelSingular = type === 'avenant' ? 'avenant' : 'remise';

  // Filtrer les ajustements par type
  const filteredAjustements = ajustements.filter(a => a.type === type);
  const total = filteredAjustements.reduce((sum, a) => sum + a.montant, 0);

  // Supprimer un ajustement
  const handleDelete = async (id: string) => {
    if (!confirm(`Supprimer ce${type === 'remise' ? 'tte' : 't'} ${labelSingular} ?`)) return;
    await onDelete(id);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="flex items-center justify-between py-2">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            {isOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <span>
              {label} ({filteredAjustements.length})
              {total > 0 && (
                <span className={`ml-2 ${type === 'avenant' ? 'text-green-500' : 'text-red-500'}`}>
                  {type === 'avenant' ? '+' : '-'}{formatMontant(total)}
                </span>
              )}
            </span>
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="pl-6 space-y-3">
          {/* Liste des ajustements existants */}
          {filteredAjustements.map((ajustement) => (
            <div
              key={ajustement.id}
              className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-md"
            >
              <div className="flex-1">
                <div className="flex items-center gap-4">
                  <span className={`font-medium ${type === 'avenant' ? 'text-green-500' : 'text-red-500'}`}>
                    {type === 'avenant' ? '+' : '-'}{formatMontant(ajustement.montant)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {formatDate(ajustement.date_ajustement)}
                  </span>
                </div>
                {ajustement.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {ajustement.description}
                  </p>
                )}
              </div>

              {!isDisabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(ajustement.id)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}

          {/* Message si vide */}
          {filteredAjustements.length === 0 && (
            <p className="text-sm text-muted-foreground py-2">
              Aucun {labelSingular} enregistré
            </p>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default AjustementsSection;
